import { Trash2 } from "lucide-react"
import { useState } from "react"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/ui/alert-dialog"
import { Button } from "~/ui/button"
import { useToast } from "~/ui/use-toast"

const unsubscribeSubscriberMutation = gql(/* GraphQL */ `
  mutation UnsubscribeSubscriber($input: LettermemeSubscriptionUnsubscribeInput!) {
    lettermemeSubscriptionUnsubscribe(input: $input) {
      lettermeme {
        id
      }
    }
  }
`)

interface RemoveSubscriberActionProps {
  userId: string
  userEmail: string
  lettermemeId: string
}

export const RemoveSubscriberAction: React.FC<RemoveSubscriberActionProps> = ({
  userId,
  userEmail,
  lettermemeId,
}) => {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()

  const [removeSubscriber, { loading }] = useSafeMutation(unsubscribeSubscriberMutation, {
    refetchQueries: ["LettermemeSubscriptions"],
  })

  const handleRemove = async () => {
    try {
      await removeSubscriber({
        variables: {
          input: {
            lettermemeId,
            userId,
          },
        },
      })
      toast({
        title: "Subscriber removed",
        description: `${userEmail} has been unsubscribed from this newsletter.`,
      })
      setOpen(false)
    } catch (e) {
      toast({
        title: "Error",
        description: "Error removing subscriber",
        variant: "destructive",
      })
      console.error(e)
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="h-8 w-8 text-gray-400 hover:text-red-500"
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove subscriber</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{userEmail}</strong> from this newsletter?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} disabled={loading}>
              {loading ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
